// TODO There's a problem with infering option type in SelectAsync component implementation, probably due to ref type declaration
import {useCallback, useEffect, useId, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {FacebookIcon, InstagramIcon, LinkedinIcon, SearchBoldIcon, XIcon, YouTubeIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {SystemModal} from "@pg-design/modal-module";
import {Components, ControlProps, MenuProps, OptionProps, SelectAsync, SelectInstance} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {debounce, uniq} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {fetchArticlesResults} from "../api/fetch_articles_results";

type ResultOption = {categoryId?: number; articlesCount: number; slug: string; label: string; type: string; value: string};
type ResultOptions = ResultOption[];
type GetArticlesOptionsDebounced = (inputValue: string, callback: (options: ResultOptions) => void) => void;
type LoadOptions = (inputValue: string, callback: (options: ResultOptions) => void) => Promise<unknown[]>;

const socialLinks = [
    {icon: FacebookIcon, url: rpAppLink.external.socialMedia.facebook},
    {icon: InstagramIcon, url: rpAppLink.external.socialMedia.instagram},
    {icon: LinkedinIcon, url: rpAppLink.external.socialMedia.linkedin},
    {icon: YouTubeIcon, url: rpAppLink.external.socialMedia.youtube},
    {icon: XIcon, url: rpAppLink.external.socialMedia.x, fillReverse: true}
];

export const ArticleSearchBar = () => {
    const articleCategories = useSelector((state: IRPStore) => state.siteMap.articleCategories);
    const {isMobile} = useUserDevice();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [cachedOptions, setCachedOptions] = useState<ResultOptions>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState<null | {value: string; label: string}>(null);
    const selectRef = useRef<SelectInstance<unknown>>(null);
    const mobileSelectRef = useRef<SelectInstance<unknown>>(null);
    const location = useLocation();
    const phrase = parseSearch(location.search)?.phrase;
    const instanceId = useId();

    const getArticleOptions = async (eventValue: string) => {
        const value = !!eventValue ? eventValue : inputValue;
        const articlesArr = await fetchArticlesResults(value);
        if (!value) {
            return [] as ResultOptions;
        }
        const articleCategoryPair = articlesArr?.results?.flatMap(({categories, id}) => categories.map((categoryId) => ({categoryId, articleId: id})));

        const categoriesIds = uniq(articleCategoryPair.map(({categoryId}) => categoryId));
        const resultOptions: ResultOptions = categoriesIds.map((categoryId) => {
            const foundCategory = articleCategories?.find(({id}) => id === categoryId);
            const label = foundCategory?.name as string;
            const slug = `${rpAppPath.article.list}kategorie/${foundCategory?.slug}?phrase=${value}`;
            const articlesCount = articleCategoryPair.filter((pair) => pair.categoryId === categoryId).length;
            return {categoryId, articlesCount, slug, label, type: "Kategoria", value: slug};
        });

        if (value && !!articlesArr.count) {
            resultOptions.unshift({
                label: value,
                articlesCount: articlesArr.count,
                slug: `${rpAppPath.article.list}?phrase=${value}`,
                value: `${rpAppPath.article.list}?phrase=${value}`,
                type: "Artykuły"
            });
        }

        const finalOptions = resultOptions.filter(({slug, label}) => !!slug && !!label).sort((a, b) => b.articlesCount - a.articlesCount);
        setCachedOptions(finalOptions);
        return finalOptions;
    };

    const getArticlesOptionsDebounced: GetArticlesOptionsDebounced = useCallback(
        debounce((inputValue, callback) => {
            //TODO: right now we need to cast each parameter of the callback, we should modify debounce's type so that it picks up types in order of passed params
            getArticleOptions(inputValue as string).then((options) => (callback as (options: ResultOptions) => void)(options));
        }, 500),
        []
    );

    useEffect(() => {
        if (!isMobile) {
            return;
        }
        if (!isMenuOpen) {
            setTimeout(() => selectRef.current?.blur(), 250);
            return;
        }
        setTimeout(() => mobileSelectRef.current?.focus(), 1);
    }, [isMenuOpen]);

    useEffect(() => {
        if (phrase) {
            setInputValue(phrase as string);
            getArticleOptions(phrase as string);
        }
    }, []);

    return (
        <div className={mainWrapperStyle}>
            <WideContent className={fieldWrapperStyle}>
                <SelectAsync
                    instanceId={instanceId}
                    ref={selectRef}
                    styles={selectStyles}
                    components={{Option, Menu, DropdownIndicator: null, Control, NoOptionsMessage}}
                    disableDropdownIndicator
                    onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                    onMenuOpen={() => setIsMenuOpen(true)}
                    menuIsOpen={isMenuOpen && !!inputValue}
                    placeholder={(phrase as string) || "Jaki temat Cię interesuje?"}
                    onInputChange={(value, meta) => {
                        if (meta.action === "input-change") {
                            setInputValue(value);
                        }
                    }}
                    inputValue={inputValue}
                    loadOptions={getArticlesOptionsDebounced as LoadOptions}
                    defaultOptions={cachedOptions}
                    onChange={() => {
                        setInputValue("");
                        setIsLoading(true);
                        setValue({label: inputValue, value: inputValue});
                    }}
                    value={value}
                    loadingMessage={() => <>Ładowanie</>}
                    className={css({width: "100%", maxWidth: "550px"})}
                />
                <div className={socialIconsWrapperStyle}>
                    {socialLinks.map(({icon: Icon, url, fillReverse}) => (
                        <a key={url} href={url} target="_blank" rel="nofollow" className={socialIconLinkStyle}>
                            {fillReverse ? (
                                <Icon size="3.8" wrapperSize="3.8" wrapperColor="#000" fill="#E1D7CD" wrapperType="circle" />
                            ) : (
                                <Icon size="4.4" wrapperSize="4.4" wrapperColor="transparent" fill="#000" wrapperType="circle" />
                            )}
                        </a>
                    ))}
                </div>
            </WideContent>
            {(isMenuOpen || isLoading) && !isMobile && (
                <DesktopBlend top="0" isActive className={flexAbsoluteCenterStyle}>
                    {isLoading && (
                        <div className={loaderWrapperStyle}>
                            <Loader padding="xl" />
                        </div>
                    )}
                </DesktopBlend>
            )}
            <SystemModal
                isOpen={isMenuOpen && !!isMobile}
                onModalClose={() => {
                    setIsMenuOpen(false);
                    selectRef.current?.blur();
                }}
                variant="fit"
                headerVariant="gray"
                className={modalContentStyle}
            >
                <SystemModal.Header className={css({paddingTop: 0})}>
                    <Text variant="body_copy_2">Szukaj</Text>
                </SystemModal.Header>
                <SystemModal.Content>
                    {!isLoading && (
                        <SelectAsync
                            instanceId={instanceId}
                            defaultOptions={cachedOptions}
                            ref={mobileSelectRef}
                            styles={selectStylesMobile}
                            components={{
                                Option,
                                DropdownIndicator: null,
                                Menu,
                                Control,
                                NoOptionsMessage,
                                MenuList: (props) => <Components.MenuList {...props} className={menuListStyle} />
                            }}
                            disableDropdownIndicator
                            onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                            onMenuOpen={() => setIsMenuOpen(true)}
                            menuIsOpen={!!inputValue}
                            placeholder="Jaki temat Cię interesuje?"
                            onInputChange={(value, meta) => {
                                if (meta.action === "input-change") {
                                    setInputValue(value);
                                }
                            }}
                            inputValue={inputValue}
                            loadOptions={getArticlesOptionsDebounced as LoadOptions}
                            onChange={() => {
                                setIsLoading(true);
                            }}
                            loadingMessage={() => <div className={flexAbsoluteCenterStyle}>Ładowanie</div>}
                        />
                    )}
                    {isLoading && (
                        <div className={mobileLoaderWrapperStyle}>
                            <Loader />
                        </div>
                    )}
                </SystemModal.Content>
            </SystemModal>
        </div>
    );
};

const Option = (props: OptionProps<unknown>) => {
    const optionProps = props as OptionProps<ResultOption>;
    return (
        <Components.Option {...props} className={separatorStyle}>
            <a href={optionProps.data.slug} className={optionLinkStyle}>
                <div className={labelContainerStyle}>
                    <Text variant="body_copy_1">{props.label}</Text>
                    <Text variant="info_txt_1" color="var(--colors-gray-700)">
                        {optionProps.data.type}
                    </Text>
                </div>
                {!!optionProps.data.articlesCount && optionProps.data.type !== "Kategoria" && (
                    <Text variant="info_txt_1" color="var(--colors-gray-600)">
                        {optionProps.data.articlesCount} artykuły
                    </Text>
                )}
            </a>
        </Components.Option>
    );
};

const Menu = (props: MenuProps) => (
    <Components.Menu {...props} className={menuStyle}>
        {props.selectProps.isLoading ? <div className={loadingCenterStyle}>Ładowanie</div> : props.children}
    </Components.Menu>
);

const Control = (props: ControlProps) => {
    return (
        <Components.Control {...props} className={controlStyle}>
            <SearchBoldIcon wrapperSize="5.6" wrapperColor="var(--colors-primary)" size="2.4" className={searchIconStyle} wrapperType="square" />
            {props.children}
        </Components.Control>
    );
};

const NoOptionsMessage = () => {
    return <div className={flexAbsoluteCenterStyle}>Brak wyników dla szukanej frazy</div>;
};

const controlStyle = css({
    borderTopLeftRadius: 2,
    borderTopRightRadius: 1,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 1,
    border: "none",
    overflow: "hidden",
    cursor: "text",
    padding: 0
});

const optionLinkStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    _hover: {
        color: "initial"
    },
    _focus: {
        color: "initial"
    }
});

const menuStyle = css({
    borderRadius: 2,
    border: "none",
    padding: 2,
    marginTop: 2,
    smDown: {
        boxShadow: "none",
        padding: 0
    }
});

const mainWrapperStyle = css({
    width: "100vw",
    maxWidth: "100%",
    backgroundColor: "#e1d7cd",
    position: "relative"
});

const fieldWrapperStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    paddingY: 2,
    paddingX: 1.5,
    gap: 2,
    md: {
        flexDirection: "row",
        justifyContent: "space-between",
        height: "110px",
        paddingY: 2,
        paddingX: 2,
        gap: 0
    }
});

const labelContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const selectStyles = {
    container: (base: object) => ({
        ...base,
        zIndex: 1001,
        width: "100%"
    }),
    control: (base: object) => ({
        ...base,
        border: "none",
        overflow: "hidden",
        cursor: "text",
        padding: 0
    })
};

const selectStylesMobile = {
    container: (base: object) => ({
        ...base,
        width: "100%"
    }),
    control: (base: object) => ({
        ...base,
        border: "1px solid var(#E1D7CD)",
        overflow: "hidden",
        cursor: "text",
        padding: 0
    })
};

const separatorStyle = css({
    "&:not(:last-of-type)": {
        borderBottom: "1px solid var(--colors-gray-200)"
    }
});

const searchIconStyle = css({
    cursor: "default",
    marginRight: 2
});

const modalContentStyle = css({
    mdDown: {
        inset: 0
    }
});

const menuListStyle = css({
    maxHeight: "initial"
});

const socialIconsWrapperStyle = css({
    display: "flex",
    gap: 2,
    md: {
        gap: 1,
        marginLeft: 4
    }
});

const socialIconLinkStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "opacity 0.2s ease",
    _hover: {
        opacity: 0.8
    }
});

const flexAbsoluteCenterStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 8
});

const mobileLoaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 4
});

const loadingCenterStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%"
});
